import { Divider, Grid, IconButton, makeStyles } from "@material-ui/core";
import AttachFileIcon from '@material-ui/icons/AttachFile';
import EmailIcon from '@material-ui/icons/Email';
import { define, Model } from "@type-r/models";
import { AttributesValues } from "@type-r/models/lib/model/updates";
import { Linked, useLinked, useModel, useModelCopy } from "@type-r/react";
import { InvoiceManagementPermission, localUserInfo, ParticipantRoles } from "api";
import { GetInvoiceStatusAsText, InvoiceModel } from "api/invoice";
import { isDate } from "lodash";
import { asCurrency, asTimelessDate, Button, CompactInput, Flex, PageHeaderTile, PageTileField, PrimaryButton, Toolbar } from "lqx-react";
import * as React from "react";
import { InvoiceStateFlow, invoiceStatusColor } from "../common";
import { DialogType } from "./dialogs";

interface InvoiceToolbarParams {
  invoice: InvoiceModel;
  prevInvoice: InvoiceModel;
  $dialog: Linked<DialogType>;
  isInvoiceChanged: boolean;
}

const getShowDisputeButton = (invoice: InvoiceModel) => {

  if (invoice.isCustomer) {
    if(invoice.isDisputedWithSupplierStatus) return false;

    if (invoice.isCustomerApprover) {
      if (!invoice.hasActiveDispute && !invoice.isDisputedStatus) return true;
      if (!invoice.hasActiveDispute) return false; //all require active disputes now / internal ones dont change the status

      let activeDispute = invoice.getActiveDispute;
      if (!invoice.isDisputedStatus && activeDispute.isInternalEscalation && activeDispute.isEscalatedByCustomer)
        return true;

      if (invoice.isDisputedWithCustomerStatus)
        return true;

      return false;
    }

    if (invoice.isCustomerWrite) {
      if(!invoice.hasActiveDispute && !invoice.isDisputedStatus) return true;
      if(!invoice.hasActiveDispute) return false; //all require active disputes now / internal ones dont change the status

      let activeDispute = invoice.getActiveDispute;
      if(invoice.isDisputedWithCustomerStatus){
        if(!activeDispute.isInternalEscalation && !activeDispute.isEscalatedByCustomer) return true;
      }

      return false;
    }   
  }

  if (invoice.isSupplier) {
    if (invoice.isDisputedWithCustomerStatus && !invoice.isSuperSupplier) return false;

    if(invoice.isSuperSupplier){
      if (!invoice.hasActiveDispute && !invoice.isDisputedStatus) return true;
      if (!invoice.hasActiveDispute) return false; //all require active disputes now / internal ones dont change the status

      let activeDispute = invoice.getActiveDispute;

      if (invoice.isDisputedWithSupplierStatus)
        return true;
      if (invoice.isDisputedWithCustomerStatus) {
        if (activeDispute.isEscalatedBySupplier)
          return true;
      }
      return false;
    }

    if (invoice.isSupplierApprover) {
      if (!invoice.hasActiveDispute && !invoice.isDisputedStatus && !invoice.isApprovalNeededStatus) return true;
      if (!invoice.hasActiveDispute) return false; //all require active disputes now / internal ones dont change the status

      let activeDispute = invoice.getActiveDispute;

      if (!invoice.isDisputedStatus && activeDispute.isInternalEscalation && activeDispute.isEscalatedBySupplier)
        return true;
      if (invoice.isDisputedWithSupplierStatus)
        return true;

      return false;
    }

    if (invoice.isSupplierWrite) {
      if (!invoice.hasActiveDispute && !invoice.isDisputedStatus && !invoice.isApprovalNeededStatus) return true;
      if (!invoice.hasActiveDispute) return false; //all require active disputes now / internal ones dont change the status

      let activeDispute = invoice.getActiveDispute;

      if (invoice.isDisputedWithSupplierStatus) {
        if (activeDispute.isInternalEscalation && activeDispute.isEscalatedBySupplier)
          return false;

        return true;
      }

      return false;
    }
  }

  return false;
}

const InvoiceToolbar = ({
  invoice,
  prevInvoice,
  $dialog,
  isInvoiceChanged
}: InvoiceToolbarParams) => {

  const classes = useStyles();
  const $acceptButtonClicked = useLinked(false);

  // will be extended with the rest of the header tickets
  const showAcceptControls = (!invoice.hasActiveDispute && invoice.isApprovalNeededStatus && invoice.isParticipantApprover)
    || invoice.canEditPromiseToPay;

  const isWriteRole = invoice.isSupplierWrite || invoice.isCustomerWrite;
  const isApproverRole = invoice.isSupplierApprover || invoice.isCustomerApprover;

  // dispute controls
  let showEscalateInternallyButton = false;
  let showDisputeButton = getShowDisputeButton(invoice);
  let showUpdateButton = false;
  let showSendBackButton = false;
  let showRejectButton = false;
  let showApproveButton = false;
  let showCloseButton = invoice.isPaymentInTransitStatus || invoice.isReconciledStatus;

  if (invoice.hasActiveDispute) {

    const activeDispute = invoice.getActiveDispute;

    if (activeDispute.isUserOnTheActiveSide) {

      showEscalateInternallyButton = isWriteRole && !activeDispute.isInternalEscalation;

      showUpdateButton =
        activeDispute.isOnlyInternalEscalation
        && (invoice.isApprovalNeededStatus || invoice.isPaymentInTransitStatus || invoice.isReconciledStatus)
        && (invoice.isSupplierApprover || invoice.isSuperSupplier);

      showRejectButton =
        activeDispute.isOnlyInternalEscalation && isApproverRole;

      showSendBackButton =
        (isApproverRole || invoice.isSuperSupplier)
        && activeDispute.isInternalEscalation
        && (invoice.isApprovalNeededStatus || invoice.isAwaitingRemittanceStatus || invoice.isPaymentInTransitStatus || invoice.isReconciledStatus);

      showApproveButton =
        !activeDispute.isOnlyInternalEscalation
        && ((!activeDispute.isInternalEscalation && isWriteRole) || isApproverRole);
    }

    if (invoice.isSuperSupplier && !activeDispute.isOnlyInternalEscalation) {
      if (activeDispute.isInternalEscalation) {
        const isOnSupplierSide = activeDispute.disputeSide === "Supplier";
        showApproveButton = isOnSupplierSide;
        showSendBackButton = isOnSupplierSide;
      } else {
        showApproveButton = true;
      }
    }
  } else {
    showEscalateInternallyButton = isWriteRole;
    showUpdateButton = invoice.isApprovalNeededStatus && invoice.isSupplierApprover;
  }

  const showDisputeControls = !localUserInfo.isAdminRead
    && (showEscalateInternallyButton
      || showDisputeButton
      || showUpdateButton
      || showRejectButton
      || showSendBackButton);
  // ------- dispute controls -------

  const showActionControls = (showAcceptControls || showApproveButton || showDisputeControls || showCloseButton)
    && invoice.invoiceStatus !== "PaymentComplete";

  const $promiseToPayDate = invoice.$.promiseToPayDateUtc.check(newDate => !$acceptButtonClicked.value || isDate(newDate));

  const showSendEmail = invoice.isSupplier;
  const showInternalStatus = invoice.isSupplier;

  const showDefaultWriteActions = localUserInfo.isAdminWrite
    || localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Customer, [InvoiceManagementPermission.CustomerAdmin, InvoiceManagementPermission.CustomerApprover, InvoiceManagementPermission.CustomerWrite], invoice.conversationId)
    || localUserInfo.hasSomePermissions("InvoiceManagement", ParticipantRoles.Supplier, [InvoiceManagementPermission.SupplierAdmin, InvoiceManagementPermission.SupplierApprover, InvoiceManagementPermission.SupplierWrite, InvoiceManagementPermission.SupplierSuperSupplier], invoice.conversationId);

  return (
    <Toolbar>
      {showActionControls ? (
        <>
          <Flex grow />
          <Grid
            container
            onClick={e => e.stopPropagation()}
            className={classes.acceptButtonContainer}
          >
            {
              showInternalStatus
                ? (<Button variant="contained" onClick={() => { $dialog.set("internalStatus"); }}>
                  Internal Status
                </Button>)
                : <></>
            }
            {
              showDefaultWriteActions
                ?
                <>
                  <Button onClick={() => { $dialog.set("comment"); }}> Add Comments </Button>
                  <IconButton aria-label="Attach" size="small" onClick={() => $dialog.set("upload")} >
                    <AttachFileIcon />
                  </IconButton>
                </>
                : <></>
            }

            {
              showSendEmail
                ? (<IconButton
                  aria-label="Send Email"
                  size="small"
                  onClick={() => $dialog.set("email-invoice")}
                >
                  <EmailIcon />
                </IconButton>)
                : null
            }

            {showAcceptControls ? (
              <>
                <Divider orientation="vertical" flexItem />

                <CompactInput
                  date
                  label="Promise to Pay"
                  minDate={new Date()}
                  $value={$promiseToPayDate}
                />
                <PrimaryButton
                  disabled={isInvoiceChanged || $acceptButtonClicked.value}
                  variant="contained"
                  onClick={async () => {
                    $acceptButtonClicked.set(true);
                    await invoice.accept(invoice.canEditPromiseToPay);
                    prevInvoice.set(invoice.attributes);
                    $acceptButtonClicked.set(false);
                  }}
                >
                  {invoice.canEditPromiseToPay ? 'Edit' : 'Accept'}
                </PrimaryButton>
              </>
            ) : null}

            {showApproveButton ? (
              <Button
                disabled={isInvoiceChanged}
                variant="contained"
                onClick={() => {
                  $dialog.set("approve");
                }}
              >
                Approve
              </Button>
            ) : null}

            {showCloseButton ? (
              <PrimaryButton
                disabled={isInvoiceChanged}
                variant="contained"
                onClick={() => $dialog.set("close")}
              >
                Close
              </PrimaryButton>
            ) : null}

            {showDisputeControls ? (
              <>
                <Divider orientation="vertical" flexItem />

                {showEscalateInternallyButton ? (
                  <Button
                    disabled={isInvoiceChanged && invoice.hasActiveDispute}
                    variant="contained"
                    onClick={() => {
                      $dialog.set("escalateInternally");
                    }}
                  >
                    Escalate Internally
                  </Button>
                ) : null}

                {showDisputeButton ? (
                  <PrimaryButton
                    color="red"
                    onClick={() => {
                      $dialog.set("dispute");
                    }}
                  >
                    Dispute
                  </PrimaryButton>
                ) : null}

                {showUpdateButton ? (
                  <Button
                    variant="contained"
                    onClick={() => {
                      $dialog.set("update");
                    }}
                  >
                    Update
                  </Button>
                ) : null}

                {showSendBackButton ? (
                  <Button
                    disabled={isInvoiceChanged}
                    variant="contained"
                    onClick={() => {
                      $dialog.set("sendBack");
                    }}
                  >
                    Send Back
                  </Button>
                ) : null}

                {showRejectButton ? (
                  <Button
                    disabled={isInvoiceChanged}
                    variant="contained"
                    onClick={() => {
                      $dialog.set("reject");
                    }}
                  >
                    Send Back(Reject)
                  </Button>
                ) : null}
              </>
            ) : null}
          </Grid>
        </>
      ) : null}
    </Toolbar>
  );
};

// using InvoiceToolbarParams because at the moment they are the same
// this should be changed when that's no longer the case
export const InvoiceHeader = ({
  invoice,
  prevInvoice,
  $dialog,
  isInvoiceChanged
}: InvoiceToolbarParams) => {
  const status = invoice.calculatedStatus;
  return (
    <PageHeaderTile
      color={invoiceStatusColor(status)}
      title={invoice.title}
      right={<InvoiceStateFlow invoice={invoice} />}
      toolbar={
        <InvoiceToolbar
          invoice={invoice}
          $dialog={$dialog}
          prevInvoice={prevInvoice}
          isInvoiceChanged={isInvoiceChanged}
        />
      }
      returnLink='/invoices'
    >
      <PageTileField label="Status">
        {GetInvoiceStatusAsText(status)}
      </PageTileField>
      <PageTileField label="Invoice Due">
        {asTimelessDate(invoice.invoiceDueDateUtc)}
      </PageTileField>
      <PageTileField label="Invoice #">{invoice.invoiceNumber}</PageTileField>
      <PageTileField label="PO #">{invoice.poNumber}</PageTileField>
      <PageTileField label="Invoice Amt">
        {asCurrency(invoice.invoicedAmount)}
      </PageTileField>
      <PageTileField label="Outstanding">
        {asCurrency(invoice.outstandingInvoiceAmount)}
      </PageTileField>
      {invoice.showPromiseToPayDate && <>
        <PageTileField label="Promise to Pay">{asTimelessDate(invoice.promiseToPayDateUtc)}</PageTileField>
      </>
      }
    </PageHeaderTile>
  );
};

const useStyles = makeStyles(theme => ({
  acceptButtonContainer: {
    width: "unset",

    "& > hr": {
      margin: theme.spacing(0, 0.5)
    },

    "& > *": {
      marginTop: "auto",
      marginBottom: "auto"
    }
  }
}));
